import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import Select from 'react-select';
import Layout from './DashboardLayout';
import { TimesheetTable } from '../components/timesheet';
import {
  teamService,
  timesheetService,
  tasksService,
  clientsService,
} from '../services';
import Loading from '../components/Loader';
import { fetchOnBoardProgress } from '../actions/user';
import {
  DateRangePickerField,
  WorkSessionTypeDropdown,
  MemberMultiSelectDropdown,
  MultiSelectItemDropdown,
} from '../components/common';
import {
  DATE_RANGE_OPTIONS,
  TIMESHEET_DATE_RANGE,
  MEMBER_VALIDATION_LIMIT,
} from '../constants';
import { getUserData } from '../helpers';
import { trackIntercom } from '../utilities/analytics';
import ReportGenerationModal from '../components/ReportGenerationModal';

class SummarizedTimesheet extends Component {
  state = {
    isLoading: true,
    siteId: [],
    project: [],
    clientId: [],
    teamMember: [],
    teamMembersList: [],
    teamGroupList: [],
    projectsList: [],
    tasksList: [],
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    timesheet: [],
    timesheetMetadata: {},
    isLoadingTimesheet: true,
    isFetchingTimesheetData: false,
    isSummarizedReport: true,
    isUpdatingProjectsList: false,
    isUpdatingTasksList: false,
    isSendingEmail: false,
    numberOfDaysSelected: 1,
    daysLimit: 90,
    clientsData: [],
    changeSource: null,
    formParams: null,
    asyncTimesheetModalDisplay: false,
  };

  pageHeading = {
    preTitle: 'Timesheet',
    title: 'Summarized Timesheet',
  };

  sTimesheetFormRef = React.createRef();

  selectInputRef = React.createRef(null);

  taskSelectRef = React.createRef(null);

  projectSelectInputRef = React.createRef();

  clientSelectInputRef = React.createRef();

  siteSelectInputRef = React.createRef();

  componentDidMount() {
    const { startDate, endDate } = this.state;
    const { permissions } = this.props;
    const memberId = getUserData('_id');
    const teamStrength = getUserData('maxMembersAllowed') || 0;
    this.getClientData();
    this.getMembersAndProjects();

    if (teamStrength <= MEMBER_VALIDATION_LIMIT) {
      this.handleGenerateData({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        clientId: [],
        fromDate: startDate,
        toDate: endDate,
        project: [],
        task: 'all',
        teamMember:
          permissions && permissions.includes('team_member_view')
            ? []
            : [memberId],
        workSessionType: 'all',
        excludeArchivedProjects: true,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { changeSource } = this.state;

    // Only act if changeSource has changed
    if (!changeSource || changeSource === prevState.changeSource) return;

    const formik = this.sTimesheetFormRef.current;
    const memberSelect = this.selectInputRef?.current?.select;
    const clientSelect = this.clientSelectInputRef?.current?.select;
    const projectSelect = this.projectSelectInputRef?.current?.select;

    const clearSelect = (selectRef) => selectRef?.clearValue();
    const resetFields = (fields) =>
      fields.forEach((f) => formik.setFieldValue(f, []));

    switch (changeSource) {
      case 'site':
        formik.setFieldValue('team', 'all');
        formik.setFieldValue('task', 'all');
        resetFields(['clientId', 'project', 'teamMember']);
        [memberSelect, clientSelect, projectSelect].forEach(clearSelect);

        this.getClientData();
        setTimeout(() => {
          this.getMembersAndProjects();
        }, 200);
        break;

      case 'client':
        formik.setFieldValue('team', 'all');
        formik.setFieldValue('task', 'all');
        resetFields(['teamMember', 'project']);
        [memberSelect, projectSelect].forEach(clearSelect);
        this.getMembersAndProjects();
        break;

      case 'project':
        formik.setFieldValue('task', 'all');
        resetFields(['teamMember']);
        clearSelect(memberSelect);
        this.getMembersWithProject();
        break;

      default:
        break;
    }

    // Reset changeSource after handling to prevent re-trigger
    this.setState({ changeSource: null });
  }

  handleSiteChange = (selectedSites) => {
    this.setState({
      siteId: selectedSites,
      isUpdatingProjectsList: true,
      changeSource: 'site',
    });
  };

  handleClientChange = (selelctedClient) => {
    this.setState({
      clientId: selelctedClient,
      changeSource: 'client',
    });
  };

  getMembersAndProjects = () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { siteId, clientId } = this.sTimesheetFormRef.current.values;
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      siteId,
      clientId,
    };
    if (siteId.length === 0 || siteId.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }
    teamService
      .get_team_members_and_projects(params)
      .then((result) => {
        const { teamMembers, projects, teams } = result.data;
        this.setState((state) => ({
          ...state,
          projectsList: [...projects],
          teamMembersList: [...teamMembers],
          teamGroupList: [...teams],
        }));
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          isUpdatingProjectsList: false,
        }));
      });
  };

  handleDateSelect = (event, picker) => {
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate).format('YYYY-MM-DD'),
      endDate: moment(picker.endDate).format('YYYY-MM-DD'),
    }));
  };

  handleDateRangeValidation = (start, end) => {
    const { daysLimit } = this.state;
    const daysSelected = Math.abs(moment(start).diff(moment(end), 'd'));
    if (daysSelected > daysLimit) {
      toast.error(
        `${TIMESHEET_DATE_RANGE.LIMIT} ${TIMESHEET_DATE_RANGE.ERROR}`
      );
    }
    this.setState((state) => ({
      ...state,
      numberOfDaysSelected: daysSelected,
    }));
  };

  getClientData = () => {
    const { siteId } = this.sTimesheetFormRef.current.values;
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      status: 'active',
      siteId,
    };
    if (siteId.length === 0 || siteId.includes('all')) {
      delete params.siteId;
    }
    clientsService
      .get_client_list(params)
      .then((clientData) => {
        if (clientData.data) {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            clientsData: [...clientData.data],
          }));
        }
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getTaskData = () => {
    if (this.sTimesheetFormRef.current) {
      const { siteId, project, teamMember } =
        this.sTimesheetFormRef.current.values;
      this.setState((state) => ({
        ...state,
        isUpdatingTasksList: true,
      }));
      const taskParams = {
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        siteId,
        members: [...teamMember],
        projects: [...project],
      };
      if (siteId.length === 0 || siteId.includes('all')) {
        delete taskParams.siteId;
      }
      if (project.length === 0 || project.includes('all')) {
        delete taskParams.projects;
      }
      if (
        teamMember === 'all' ||
        teamMember === '' ||
        teamMember.length === 0 ||
        teamMember.includes('all')
      ) {
        delete taskParams.members;
      }

      tasksService
        .get_tasks_list(taskParams)
        .then((result) => {
          const { tasks } = result.data;
          this.setState((state) => ({
            ...state,
            isLoading: false,
            tasksList: [...tasks],
            isUpdatingTasksList: false,
          }));
        })
        .finally(() => {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            isUpdatingTasksList: false,
          }));
        });
    }
  };

  handleProjectChange = (selelctedProjectList) => {
    this.setState({
      changeSource: 'project',
      project: selelctedProjectList,
    });
  };

  getMembersWithProject = () => {
    const { siteId, clientId, project } = this.state;

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
      siteId,
      clientId,
    };
    if (project.length > 0) {
      params.projectId = project;
    }
    if (project.length === 0 || project.includes('all')) {
      delete params.projectId;
    }
    if (siteId.length === 0 || siteId.includes('all')) {
      delete params.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete params.clientId;
    }

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    teamService
      .get_team_members_with_project(params)
      .then((response) => {
        const { members, teams } = response.data && response.data[0];
        this.setState((state) => ({
          ...state,
          teamMembersList: [...members],
          teamGroupList: [...teams],
        }));
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleGenerateData = (formdata, asyncOnly = false) => {
    if (!asyncOnly) {
      this.setState((state) => ({
        ...state,
        isFetchingTimesheetData: true,
      }));
    }
    const { startDate, endDate } = this.state;
    const { permissions } = this.props;
    const params = {
      teamOwnerId: formdata.teamOwnerId,
      siteId: formdata.siteId,
      clientId: formdata.clientId,
      teamGroupId: formdata.team,
      teamMemberId: formdata.teamMember,
      projectId: formdata.project,
      taskId: [formdata.task].flat(),
      workSessionType: formdata.workSessionType,
      fromDate: startDate.concat(' 00:00:00'),
      toDate: endDate.concat(' 23:59:59'),
      summarizedTimesheet: true,
      excludeArchivedProjects: formdata.excludeArchivedProjects,
    };

    if (
      (params.siteId && params.siteId.length === 0) ||
      (params.siteId && params.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    if (
      (params.clientId && params.clientId.length === 0) ||
      (params.clientId && params.clientId.includes('all'))
    ) {
      delete params.clientId;
    }
    if (
      (permissions && !permissions.includes('team_member_view')) ||
      params.teamMemberId === 'all' ||
      params.teamMemberId === '' ||
      (params.teamMemberId && params.teamMemberId.length === 0) ||
      (params.teamMemberId && params.teamMemberId.includes('all'))
    ) {
      delete params.teamMemberId;
    }
    if (params.teamGroupId === 'all') {
      delete params.teamGroupId;
    }
    if (
      (params.projectId && params.projectId.length === 0) ||
      (params.projectId && params.projectId.includes('all'))
    ) {
      delete params.projectId;
    }
    if (formdata.task === 'all') {
      delete params.taskId;
    }
    if (formdata.workSessionType === 'all') {
      delete params.workSessionType;
    }
    // Store form data on formParams for sending email report and async export
    this.setState(() => ({
      formParams: { ...params },
    }));

    if (asyncOnly) return;

    const { getOnBoardingProgress } = this.props;
    timesheetService
      .get_timesheet_data(params)
      .then((response) => {
        trackIntercom('Viewed timesheet report (summarized)');
        const subscriptionDetails = getUserData('subscriptionDetails');
        if (subscriptionDetails.currentPlan === 'trial') {
          getOnBoardingProgress();
        }
        const {
          data: { metadata, timesheetDetails },
        } = response;

        this.setState((state) => ({
          ...state,
          isFetchingTimesheetData: false,
          timesheet: timesheetDetails,
          timesheetMetadata: metadata,
          isLoadingTimesheet: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isFetchingTimesheetData: false,
          isLoadingTimesheet: false,
        }));
      });
  };

  handleEmailReport = () => {
    const { formParams } = this.state;
    this.setState((state) => ({
      ...state,
      isSendingEmail: true,
    }));
    timesheetService
      .email_timesheet_report({ ...formParams })
      .then((response) => {
        toast.success('Timesheet has been emailed successfully');
        this.setState((state) => ({
          ...state,
          isSendingEmail: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isSendingEmail: false,
        }));
      });
  };

  handleAsyncTimesheetModalDisplay = (modalDisplay) => {
    this.setState({ asyncTimesheetModalDisplay: modalDisplay });
  };

  handleMemberChange = () => {
    if (this.taskSelectRef.current) {
      this.sTimesheetFormRef.current.setFieldValue('task', 'all');
      this.taskSelectRef.current.select.clearValue();
    }
  };

  render() {
    const {
      teamMembersList,
      projectsList,
      tasksList,
      startDate,
      endDate,
      timesheet,
      timesheetMetadata,
      isLoadingTimesheet,
      isFetchingTimesheetData,
      isSummarizedReport,
      isLoading,
      isUpdatingProjectsList,
      isUpdatingTasksList,
      isSendingEmail,
      numberOfDaysSelected,
      daysLimit,
      teamGroupList,
      clientsData,
      asyncTimesheetModalDisplay,
      formParams,
    } = this.state;
    const { permissions, features, hierarchy } = this.props;

    // Team owner ID
    const teamOwnerId = localStorage.getItem('teamOwnerId');
    const teamStrength = getUserData('maxMembersAllowed') || 0;
    // Validate form only if not a member and for accounts which has large size than specified limit
    const isValidateForm =
      permissions &&
      permissions.includes('team_member_view') &&
      teamStrength > MEMBER_VALIDATION_LIMIT;

    const timesheetValSchemaLargeAccounts = Yup.object({
      teamMember: Yup.array()
        .required('Please select at least one team member')
        .min(1, 'Please select a team member'),
    });

    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            teamOwnerId,
            siteId: [],
            clientId: [],
            teamMember: isValidateForm ? '' : [],
            team: 'all',
            project: [],
            task: 'all',
            fromDate: startDate,
            toDate: endDate,
            excludeArchivedProjects: true,
          }}
          validationSchema={isValidateForm && timesheetValSchemaLargeAccounts}
          onSubmit={(values) => {
            this.handleGenerateData(values);
          }}
          innerRef={this.sTimesheetFormRef}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Row>
                {hierarchy?.sites?.length > 0 && (
                  <Col md={6} lg={3}>
                    <MultiSelectItemDropdown
                      id="siteSelectInputRef"
                      name="siteId"
                      labelText="Sites"
                      fromArray={hierarchy.sites.map((site) => ({
                        value: site._id,
                        label: site.siteName,
                      }))}
                      formProps={formikprops}
                      isDisabled={hierarchy.sites.length < 1 && true}
                      innerRef={this.siteSelectInputRef}
                      isAllItemOption
                      isAllItemValuesWithNoArray
                      showErrorMessage
                      onChangeAction={this.handleSiteChange}
                    />
                  </Col>
                )}
                {permissions &&
                  permissions.includes('client_manage') &&
                  clientsData?.length > 0 && (
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="clientSelectInputRef"
                        name="clientId"
                        labelText="Clients"
                        fromArray={clientsData.map((client) => ({
                          value: client._id,
                          label: client.name,
                        }))}
                        formProps={formikprops}
                        isDisabled={clientsData.length < 1 && true}
                        innerRef={this.clientSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleClientChange}
                      />
                    </Col>
                  )}
                <Col md={6} lg={3}>
                  <MultiSelectItemDropdown
                    id="projectSelectInputRef"
                    name="project"
                    labelText="Projects"
                    fromArray={projectsList.map((project) => ({
                      value: project._id,
                      label: project.title,
                    }))}
                    formProps={formikprops}
                    isDisabled={projectsList.length < 1 && true}
                    innerRef={this.projectSelectInputRef}
                    isAllItemOption
                    isAllItemValuesWithNoArray
                    showErrorMessage
                    onChangeAction={this.handleProjectChange}
                  />
                </Col>
                {permissions &&
                  permissions.includes('team_member_view') &&
                  permissions.includes('others_report_view') && (
                    <Col md={6} lg={3}>
                      <MemberMultiSelectDropdown
                        id="selectInputRef"
                        name="teamMember"
                        labelText="Team Member(s)"
                        fromArray={teamMembersList}
                        formProps={formikprops}
                        isDisabled={teamMembersList.length < 1 && true}
                        innerRef={this.selectInputRef}
                        isAllTeamMemberOption={!isValidateForm}
                        isAllTeamMemberValuesWithNoArray={!isValidateForm}
                        requiredValueAs="userId"
                        isRequiredField={!!isValidateForm}
                        showErrorMessage
                        onChangeAction={this.handleMemberChange}
                      />
                    </Col>
                  )}
                {permissions && permissions.includes('team_member_view') && (
                  <Col md={6} lg={3}>
                    <Form.Group controlId="team">
                      <Form.Label>Team Group</Form.Label>
                      <Field
                        className="form-select"
                        as="select"
                        name="team"
                        onChange={(e) => {
                          formikprops.handleChange(e);
                        }}
                      >
                        <option value="all">All</option>
                        {teamGroupList &&
                          teamGroupList.length > 0 &&
                          teamGroupList.map((teamGroup, index) => (
                            <option
                              key={`teamGroup${index}`}
                              value={teamGroup._id}
                            >
                              {teamGroup.name}
                            </option>
                          ))}
                      </Field>
                    </Form.Group>
                  </Col>
                )}

                <Col md={6} lg={3}>
                  <Form.Group controlId="task" className="mb-2">
                    <Form.Label>Task</Form.Label>
                    <Select
                      ref={this.taskSelectRef}
                      onMenuOpen={() => {
                        this.getTaskData();
                      }}
                      options={
                        !isUpdatingTasksList && tasksList?.length > 0
                          ? tasksList.map((item) => ({
                              label: item.task,
                              value: item._id,
                            }))
                          : []
                      }
                      isLoading={isUpdatingTasksList}
                      className="basic-multi-select"
                      placeholder="Select Task"
                      name="task"
                      isClearable
                      defaultValue={{
                        label: 'All',
                        value: 'all',
                      }}
                      onChange={(event) => {
                        if (!event) {
                          formikprops.setFieldValue('task', 'all');
                        } else {
                          formikprops.setFieldValue('task', event.value);
                        }
                      }}
                      noOptionsMessage={() => 'No task available'}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: '#0649b5',
                          neutral10: '#edf2f9',
                          neutral20: '#d2ddec',
                          neutral30: '#d2ddec',
                          neutral50: '#b1c2da',
                          neutral80: '#12263f',
                          dangerLight: '#DEEBFF',
                          danger: '#12263f',
                        },
                      })}
                    />
                  </Form.Group>
                </Col>

                {features && features.isMobileTracking && (
                  <Col md={6} lg={3}>
                    <WorkSessionTypeDropdown />
                  </Col>
                )}

                <Col md={6} lg={3}>
                  <DateRangePickerField
                    handleDateSelect={this.handleDateSelect}
                    showRangeOptions
                    preDefinedRanges={DATE_RANGE_OPTIONS}
                    handleDateRangeValidation={this.handleDateRangeValidation}
                    isValidate
                    numberOfDaysSelected={numberOfDaysSelected}
                    daysLimit={daysLimit}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="excludeArchivedProjects">
                    <Form.Check
                      name="excludeArchivedProjects"
                      type="checkbox"
                      label="Exclude archived projects"
                      checked={formikprops.values.excludeArchivedProjects}
                      className="pt-3"
                      onChange={() =>
                        formikprops.setFieldValue(
                          'excludeArchivedProjects',
                          !formikprops.values.excludeArchivedProjects
                        )
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <div className="d-flex gap-2 mb-5">
                      <Button
                        variant="primary"
                        type="Submit"
                        disabled={
                          isUpdatingProjectsList ||
                          numberOfDaysSelected > daysLimit
                        }
                      >
                        Generate
                      </Button>
                      <Button
                        variant="outline-primary"
                        disabled={isUpdatingProjectsList}
                        onClick={() => {
                          this.handleGenerateData(
                            this.sTimesheetFormRef.current.values,
                            true
                          );
                          this.handleAsyncTimesheetModalDisplay(true);
                        }}
                      >
                        Download Excel
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        {!isLoadingTimesheet && (
          <TimesheetTable
            timesheetMetadata={timesheetMetadata}
            timesheet={timesheet}
            startDate={startDate}
            endDate={endDate}
            isSummarizedReport={isSummarizedReport}
            handleEmailReport={this.handleEmailReport}
            isMobileTracking={features && features.isMobileTracking}
            isSendingEmail={isSendingEmail}
          />
        )}

        <ReportGenerationModal
          showModal={asyncTimesheetModalDisplay}
          setShowModal={this.handleAsyncTimesheetModalDisplay}
          formParams={formParams}
          dateRange={{ startDate, endDate }}
          permissions={permissions}
          handleLoader={(status) =>
            this.setState({ isFetchingTimesheetData: status })
          }
          isLoading={isFetchingTimesheetData}
          reportType="summarized_timesheet"
        />

        {/* Loader */}
        {(isLoading || isFetchingTimesheetData) && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { permissionsReducer, featuresReducer, hierarchy } = state;
  return {
    permissions: permissionsReducer,
    features: featuresReducer,
    hierarchy,
  };
};

const mapDispatchToProps = () => ({
  getOnBoardingProgress: fetchOnBoardProgress,
});

SummarizedTimesheet.propTypes = {
  getOnBoardingProgress: PropTypes.func,
  permissions: PropTypes.array,
  features: PropTypes.object,
  hierarchy: PropTypes.object,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps()
)(SummarizedTimesheet);
